import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { IconCheck, IconCopy, IconLink } from "@tabler/icons-react";

import type { ProgramDetails } from "@/services/program.service";
import { formatNumber } from "@/utils/formatters";
import { cn } from "@/lib/utils";

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
    } catch (err) {
      console.error("Failed to copy:", err);
      toast.error("Couldn't copy. Select the text and copy it yourself.");
    }
  };
  return { copied, copy };
}

/** A small outline button: the one place on the strip that asks to be clicked. */
function CopyButton({
  label,
  done,
  onClick,
  icon,
}: {
  label: string;
  done: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        done
          ? "border-brand-600 bg-brand-600 text-brand-contrast"
          : "border-border bg-card text-foreground hover:border-brand-600 hover:text-brand-700"
      )}
    >
      <span className="[&>svg]:size-3.5">{done ? <IconCheck stroke={2} /> : icon}</span>
      {done ? "Copied" : label}
    </button>
  );
}

/**
 * What the affiliate shares, as a cell of the earnings card: the code (or
 * link) at the same size as the money, with text actions under it.
 */
export function CodeCell({ program, className }: { program: ProgramDetails; className?: string }) {
  const { copied, copy } = useCopy();
  const viaLink = program.attributionMethod === "SNEAKYLINK";
  const code = program.affiliateEnrollment?.code;
  const link = program.sneakylink?.storeUrl || program.sneakylink?.url;
  const discountUrl =
    code && program.website
      ? `${program.website}${program.shortDiscountUrl ? `/${code}` : `/discount/${code}`}`
      : null;
  const clicks = program.sneakylink?.clickCount ?? 0;
  const conversions = program.sneakylink?.conversionCount ?? 0;

  return (
    <div className={cn(className, "bg-brand-600/[0.06] dark:bg-brand-600/10")}>
      <div className="text-[13px] font-medium text-foreground">{viaLink ? "Your link" : "Your code"}</div>
      {viaLink ? (
        link ? (
          <>
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              title={link}
              className="block truncate text-xl font-semibold tracking-tight text-brand-700 underline-offset-4 hover:underline dark:text-brand-400"
            >
              {link.replace(/^https?:\/\//, "")}
            </a>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
              <CopyButton
                label="Copy link"
                icon={<IconLink stroke={1.75} />}
                done={copied === "link"}
                onClick={() => copy("link", link)}
              />
              {clicks > 0 && (
                <span>
                  {formatNumber(clicks)} click{clicks === 1 ? "" : "s"}, {formatNumber(conversions)} order
                  {conversions === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="text-sm text-muted-foreground">Being set up</div>
        )
      ) : code ? (
        <>
          <div className="truncate text-3xl font-semibold tabular-nums tracking-tight text-brand-700 dark:text-brand-400" title={code}>
            {code}
          </div>
          <div className="mt-1 flex flex-wrap gap-2">
            <CopyButton
              label="Copy code"
              icon={<IconCopy stroke={1.75} />}
              done={copied === "code"}
              onClick={() => copy("code", code)}
            />
            {discountUrl && (
              <CopyButton
                label="Copy discount link"
                icon={<IconLink stroke={1.75} />}
                done={copied === "url"}
                onClick={() => copy("url", discountUrl)}
              />
            )}
          </div>
        </>
      ) : (
        <div className="text-sm text-muted-foreground">Being set up</div>
      )}
    </div>
  );
}
